"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactFinalForm = require("react-final-form");
var _orderBy = _interopRequireDefault(require("lodash/orderBy"));
var _components = require("@folio/stripes/components");
var CUSTOM_PROPERTY_TYPES = _interopRequireWildcard(require("../../constants/customProperties"));
var _hooks = require("../../hooks");
var _NumberField = _interopRequireDefault(require("../../NumberField"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const CustomPropertyField = _ref => {
  let {
    availableCustomProperties,
    customProperty,
    customPropertyType,
    customProperties,
    index,
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    labelOverrides = {},
    name,
    onChange,
    value,
    setCustomProperties
  } = _ref;
  const kintIntl = (0, _hooks.useKintIntl)(passedIntlKey, passedIntlNS);
  // Set up the validator for the customProperty
  const customPropertyValidator = (fieldValue, allValues) => {
    const {
      note,
      publicNote,
      value: currentValue
    } = allValues?.customProperties?.[customProperty?.value]?.[0] ?? {};
    if (note && !currentValue || publicNote && !currentValue) {
      if (customProperty.type === CUSTOM_PROPERTY_TYPES.DECIMAL_CLASS_NAME || customProperty.type === CUSTOM_PROPERTY_TYPES.INTEGER_CLASS_NAME) {
        return kintIntl.formatKintMessage({
          id: 'customProperties.errors.customPropertyNoteInvalidNumber',
          overrideValue: labelOverrides.customPropertyNoteInvalidNumberError
        });
      } else {
        return kintIntl.formatKintMessage({
          id: 'customProperties.errors.customPropertyNoteWithoutValue',
          overrideValue: labelOverrides.customPropertyNoteWithoutValueError
        });
      }
    }
    if (customProperty.type === CUSTOM_PROPERTY_TYPES.DECIMAL_CLASS_NAME) {
      const regexp = /^-?[\d]*(\.[\d]{0,2})?$/;
      return fieldValue && !regexp.test(fieldValue) ? kintIntl.formatKintMessage({
        id: 'errors.maxTwoDecimals',
        overrideValue: labelOverrides.maxTwoDecimalsError
      }) : undefined;
    }
    if (customProperty.type === CUSTOM_PROPERTY_TYPES.INTEGER_CLASS_NAME) {
      const min = Number.MIN_SAFE_INTEGER;
      const max = Number.MAX_SAFE_INTEGER;
      return fieldValue && (fieldValue > max || fieldValue < min) ? kintIntl.formatKintMessage({
        id: 'errors.valueNotInRange',
        overrideValue: labelOverrides.valueNotInRangeError
      }, {
        min,
        max
      }) : undefined;
    }
    if (!customProperty.primary && !currentValue) {
      return kintIntl.formatMessage({
        id: 'stripes-core.label.missingRequiredField'
      });
    }
    return undefined;
  };
  const getCustomProperty = customPropertyValue => {
    return availableCustomProperties.find(cp => cp.value === customPropertyValue);
  };
  const renderCustomPropertyName = () => {
    const unsetCustomProperties = availableCustomProperties.filter(t => {
      // Only optional properties can be set
      if (t.primary) return false;
      const custPropValue = value[t.value];

      // The customProperty is unset and has no value.
      if (custPropValue === undefined) return true;

      // The customProperty is set but is marked for deletion. Allow reuse.
      if (custPropValue[0]?._delete) return true;
      return false;
    });
    const customPropertyValue = value[customProperty.value];
    const id = customPropertyValue?.[0]?.id;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Select, {
      autoFocus: !id,
      dataOptions: [customProperty, ...unsetCustomProperties] // The selected customProperty, and the available unset customProperties
      ,
      id: `edit-customproperty-${index}-name`,
      label: kintIntl.formatKintMessage({
        id: 'customProperties.name',
        overrideValue: labelOverrides.name
      }),
      onChange: e => {
        const newValue = e.target.value;

        // Update `state.customProperties` which controls what customProperties are being edited.
        const newCustomProperties = [...customProperties];
        newCustomProperties[index] = getCustomProperty(newValue);
        setCustomProperties(newCustomProperties);

        // Update final-form (which tracks what the values for a given customProperty are) because
        // in essence we're deleting a customProperty and creating a new customProperty.
        // We do this by 1) marking the current customProperty for deletion and 2) initing
        // the new customProperty to an empty object.
        const currentValue = value[customProperty.value] ? value[customProperty.value][0] : {};
        onChange({
          ...value,
          [customProperty.value]: [{
            id: currentValue.id,
            _delete: true
          }],
          [newValue]: [{}]
        });
      },
      required: true,
      value: customProperty.value
    });
  };
  const renderCustomPropertyValue = () => {
    const currentValue = value[customProperty.value] ? value[customProperty.value][0] : {};
    const min = Number.MIN_SAFE_INTEGER;
    const max = Number.MAX_SAFE_INTEGER;

    // Default change handler
    let handleChange = e => {
      onChange({
        ...value,
        [customProperty.value]: [{
          ...currentValue,
          _delete: e.target.value === '',
          // Delete customProperty if removing the value.
          value: e.target.value // send down the number for integers and decimal types
        }]
      });
    };
    // Figure out which component we're rendering and specify its unique props.
    let fieldProps;
    switch (customProperty.type) {
      case CUSTOM_PROPERTY_TYPES.REFDATA_CLASS_NAME:
        fieldProps = {
          component: _components.Select,
          // don't order dataOptions here, notSet should always be first in list
          dataOptions: customProperty.options,
          format: v => v?.value
        };
        break;
      case CUSTOM_PROPERTY_TYPES.MULTI_REFDATA_CLASS_NAME:
        fieldProps = {
          component: _components.MultiSelection,
          // The "not set" value is not relevant for a multi select
          dataOptions: (0, _orderBy.default)(customProperty.options?.filter(opt => !!opt?.value), 'label'),
          renderToOverlay: true // FIXME Switch to usePortal for stripes v10
        };
        // MultiSelection passes the changed array as a value not an event
        handleChange = refdataArray => {
          onChange({
            ...value,
            [customProperty.value]: [{
              ...currentValue,
              _delete: refdataArray?.length === 0 ? true : undefined,
              // Delete customProperty if removing all values.
              value: refdataArray
            }]
          });
        };
        break;
      case CUSTOM_PROPERTY_TYPES.INTEGER_CLASS_NAME:
      case CUSTOM_PROPERTY_TYPES.DECIMAL_CLASS_NAME:
        fieldProps = {
          component: _NumberField.default,
          max,
          min,
          step: 'any'
        };
        handleChange = e => {
          onChange({
            ...value,
            [customProperty.value]: [{
              ...currentValue,
              _delete: e.target.value === '' ? true : undefined,
              // Delete customProperty if removing the value.
              value: e.target.value
            }]
          });
        };
        break;
      case CUSTOM_PROPERTY_TYPES.TEXT_CLASS_NAME:
        fieldProps = {
          component: _components.TextArea,
          parse: v => v // Lets us send an empty string instead of `undefined`
        };
        break;
      case CUSTOM_PROPERTY_TYPES.DATE_CLASS_NAME:
        fieldProps = {
          component: _components.Datepicker,
          backendDateStandard: 'YYYY-MM-DD',
          timeZone: 'UTC',
          usePortal: true
        };
        break;
      default:
        fieldProps = {
          component: _components.TextField
        };
        break;
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactFinalForm.Field, {
      "data-test-customproperty-value": true,
      id: `edit-customproperty-${index}-value`,
      label: kintIntl.formatKintMessage({
        id: 'customProperties.value',
        overrideValue: labelOverrides.value
      }),
      name: `${name}.${customProperty.value}[0].value`,
      onChange: handleChange,
      required: !customProperty.primary,
      validate: (fieldValue, allValues) => customPropertyValidator(fieldValue, allValues),
      ...fieldProps
    });
  };
  const renderCustomPropertyVisibility = () => {
    const customPropertyObject = value[customProperty.value]?.[0] ?? {};
    const handleChange = e => {
      onChange({
        ...value,
        [customProperty.value]: [{
          ...customPropertyObject,
          internal: e.target.value
        }]
      });
    };
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Select, {
      "data-test-customproperty-visibility": true,
      dataOptions: [{
        value: true,
        label: kintIntl.formatKintMessage({
          id: 'customProperties.internalTrue',
          overrideValue: labelOverrides.internalTrue
        })
      }, {
        value: false,
        label: kintIntl.formatKintMessage({
          id: 'customProperties.internalFalse',
          overrideValue: labelOverrides.internalFalse
        })
      }],
      id: `edit-customproperty-${index}-visibility`,
      label: kintIntl.formatKintMessage({
        id: 'customProperties.visibility',
        overrideValue: labelOverrides.visibility
      }),
      onChange: handleChange,
      value: customPropertyObject?.internal ?? customProperty.defaultInternal
    });
  };
  const renderCustomPropertyNoteInternal = () => {
    const customPropertyObject = value[customProperty.value]?.[0] ?? {};
    const handleChange = e => {
      onChange({
        ...value,
        [customProperty.value]: [{
          ...customPropertyObject,
          note: e.target.value
        }]
      });
    };
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.TextArea, {
      "data-test-customproperty-note": true,
      id: `edit-customproperty-${index}-internal-note`,
      label: kintIntl.formatKintMessage({
        id: 'customProperties.internalNote',
        overrideValue: labelOverrides.internalNote
      }),
      onChange: handleChange,
      value: customPropertyObject?.note
    });
  };
  const renderCustomPropertyNotePublic = () => {
    const customPropertyObject = value[customProperty.value]?.[0] ?? {};
    const handleChange = e => {
      onChange({
        ...value,
        [customProperty.value]: [{
          ...customPropertyObject,
          publicNote: e.target.value
        }]
      });
    };
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.TextArea, {
      "data-test-customproperty-public-note": true,
      id: `edit-customproperty-${index}-public-note`,
      label: kintIntl.formatKintMessage({
        id: 'customProperties.publicNote',
        overrideValue: labelOverrides.publicNote
      }),
      onChange: handleChange,
      value: customPropertyObject?.publicNote
    });
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_jsxRuntime.Fragment, {
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      "data-testid": "customPropertyField",
      children: [customPropertyType === 'optional' && /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Row, {
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Col, {
          xs: 12,
          children: renderCustomPropertyName()
        })
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_components.Row, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Col, {
          md: 6,
          xs: 12,
          children: renderCustomPropertyValue()
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Col, {
          md: 6,
          xs: 12,
          children: renderCustomPropertyNoteInternal()
        })]
      }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_components.Row, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Col, {
          md: 6,
          xs: 12,
          children: renderCustomPropertyVisibility()
        }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Col, {
          md: 6,
          xs: 12,
          children: renderCustomPropertyNotePublic()
        })]
      })]
    })
  });
};
CustomPropertyField.propTypes = {
  availableCustomProperties: _propTypes.default.arrayOf(_propTypes.default.object),
  customProperty: _propTypes.default.object,
  customPropertyType: _propTypes.default.string,
  customProperties: _propTypes.default.arrayOf(_propTypes.default.object),
  index: _propTypes.default.number,
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  labelOverrides: _propTypes.default.object,
  name: _propTypes.default.string,
  onChange: _propTypes.default.func,
  setCustomProperties: _propTypes.default.func,
  value: _propTypes.default.object
};
var _default = exports.default = CustomPropertyField;